<script setup lang="ts">
import Loader from '@components/Loader.vue';
import routes from '@routes';
import Request from '@components/RequestsTable/Request.vue';
import Echo from 'laravel-echo';

import { useDossiersStore } from '@stores/useDossiersStore';
import { StateEnum, SortOrder } from '@graphql/enums/dossiers';
import { useAuthStore } from '@stores/useAuthStore';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import type { DropDownOption } from '@graphql/types';

const { $echo } = useNuxtApp();
const { fetchDossier, replaceDossier, setDossiers, setMoreDossiers, setOrderFilter, setStatusFilter, setSearchedTerm, resetPage } = useDossiersStore();
const { searchTerm, paginatorTotal, dossiers: fetchedDossiers,
        searchedDossiers, page, selectedStatusFilter, selectedOrderDate, perPage } = toRefs(useDossiersStore());
const { user } = toRefs(useAuthStore());

const isFetching = ref<boolean>(false);
const isSearching = ref<boolean>(false);
const dossiersTable = ref<HTMLDivElement>();
const observer = ref<IntersectionObserver>();
const limit = ref<number>(10);
const instance = getCurrentInstance();

const requestsStatus: DropDownOption[] = [
  {
    label: 'Alle aanvragen',
    value: '',
  },
  {
    label: 'Actieve aanvragen',
    value: StateEnum.ACTIVE,
  },
  {
    label: 'Afgeronde aanvragen',
    value: StateEnum.COMPLETED,
  },
  {
    label: 'Gestopte aanvragen',
    value: StateEnum.STOPPED,
  },
];

const requestsOrderDate: DropDownOption[] = [
  {
    label: 'Nieuwste eerst',
    value: SortOrder.DESC,
  },
  {
    label: 'Oudste eerst',
    value: SortOrder.ASC,
  },
];

const isLastPage = computed(() => fetchedDossiers.value.length < (page.value * limit.value));
const dossiers = computed(() => (searchTerm.value ? searchedDossiers.value : fetchedDossiers.value));

const fetchNextDossiers = async () => {
  await setMoreDossiers(limit.value).catch(async () => {
    const toast = await useToast({
      title: 'Let op',
      message: 'Er is het mis gegaan met het ophalen van de aanvragen.',
      duration: 5000,
      theme: 'error',
    });
    toast.present();
  });
};

const handleStatusFilter = (option: DropDownOption) => {
  resetPage();
  setStatusFilter(option);
  setDossiers();
};

const handleDateFilter = (option: DropDownOption) => {
  resetPage();
  setOrderFilter(option);
  setDossiers();
};

const handleSearchedProperties = async (searchInput: string) => {
  isSearching.value = true;

  resetPage();
  setSearchedTerm(searchInput);
  await setDossiers();

  isSearching.value = false;
};

const onSearchInput = (searchInput: string) => {
  const callback = () => handleSearchedProperties(searchInput);

  if (instance) {
    debounce(callback, instance.uid);
  }
};

const onDateFilter = () => {
  if (selectedOrderDate.value.value === 'ASC') {
    handleDateFilter(requestsOrderDate[0]);
  } else {
    handleDateFilter(requestsOrderDate[1]);
  }
};

const observerCallback = async (entries: IntersectionObserverEntry[]) => {
  if (entries[0].intersectionRatio <= 0) {
    return;
  }

  isFetching.value = true;
  page.value++;

  await fetchNextDossiers();

  isFetching.value = false;
};

const updateDossier = async (data: { dossier_id: number }) => {
  if (!dossiers.value.some(old => old.id === data.dossier_id.toString())) return;

  const dossier = await fetchDossier(data.dossier_id);

  if (dossier) {
    replaceDossier(dossier);
  }
};

($echo as Echo).private(`advisor-${user.value?.id}`)
  .listen('.statusUpdated', updateDossier);

onMounted(async () => {
  if (dossiersTable.value) {
    observer.value = new IntersectionObserver(observerCallback);
    observer.value?.observe(dossiersTable.value);
  }
});

onUnmounted(() => {
  observer.value?.disconnect();
});
</script>

<template>
  <div class="size-full flex-1 bg-white rounded-3xl flex flex-col blue-shadow">
    <div class="flex flex-col justify-between gap-4 p-4 pb-6 sm:px-6 md:!flex-row md:items-center">
      <h2 class="text-lg text-start font-semibold shrink-0 pb-8 md:!pb-0">
        <FontAwesomeIcon
          class="bottom-3 left-4 pr-3 text-3xl"
          :icon="['fad', 'files']"
          size="xl"
        />
        Aanvragen ({{ paginatorTotal }})
      </h2>
      <div class="relative grow flex flex-col justify-end gap-5 md:!flex-row">
        <div class="w-auto xl:!w-80">
          <UISearchBar
            name="zoeken"
            data-test-id="intermediair-search-input"
            :model-value="searchTerm"
            @update:model-value="onSearchInput"
          />
        </div>
        <UIDropDown
          name="request-statusses"
          aria-label="Filter op status"
          :options="requestsStatus"
          :model-value="selectedStatusFilter"
          data-test-id="intermediair-dropdown-aanvragen"
          class="shrink-0 w-full md:!w-64"
          @update:model-value="handleStatusFilter"
        />
        <UIDropDown
          name="date-order-dropdown"
          aria-label="Filter op datum"
          class="shrink-0 w-full md:!hidden"
          :options="requestsOrderDate"
          :model-value="selectedOrderDate"
          data-test-id="requests-date-order-dropdown"
          @update:model-value="handleDateFilter"
        />
      </div>
    </div>
    <div v-if="(dossiers.length > 0 || searchTerm) && !isSearching" class="w-full px-4 sm:py-4 sm:px-6 md:!p-0">
      <div class="hidden items-center gap-x-6 pb-5 w-full font-semibold border-b border-gray-50 md:!grid md:grid-cols-12">
        <div class="col-span-4 grid items-center grid-cols-5 gap-x-2">
          <div />
          <div class="col-span-2 flex items-center gap-3">
            Nummer
          </div>
          <div class="col-span-2 flex items-center gap-3 cursor-pointer" @click="onDateFilter">
            Datum
            <div class="flex flex-col">
              <FontAwesomeIcon
                v-if="selectedOrderDate.value === 'ASC'"
                class="opacity-50"
                :icon="['fad', 'caret-up']"
                size="lg"
              />
              <FontAwesomeIcon
                v-if="selectedOrderDate.value === 'DESC'"
                class="opacity-50"
                :icon="['fad', 'caret-down']"
                size="lg"
              />
            </div>
          </div>
        </div>
        <div class="col-span-3">Object</div>
        <div class="col-span-4 flex items-center gap-3">Status</div>
      </div>
      <Request
        v-for="(dossier, index) in dossiers"
        :key="dossier.id"
        :dossier="dossier"
        :index="index"
      />
      <div v-if="isLastPage && !isFetching && !searchTerm && !(paginatorTotal <= perPage)" class="text-gray-700 text-small-strong text-center p-4">
        Einde van resultaten
      </div>
    </div>
    <div v-if="isSearching" class="p-6 grid place-items-center">
      <UIMogelijkLoader class="h-40 w-fit" />
    </div>
    <div v-else-if="dossiers.length === 0 && !isFetching" class="flex flex-col gap-5 flex-1 justify-center items-center text-small-strong text-gray-700 text-center">
      <FontAwesomeIcon
        class="opacity-30"
        :icon="['fad', 'shop-slash']"
        size="3x"
      />
      Geen resultaten gevonden
      <UIButton
        class="w-full sm:w-fit"
        @click="$router.push({name: routes.quickscan})"
      >
        Start quickscan
        <template #icon-end>
          <FontAwesomeIcon :icon="['fas', 'arrow-right']" />
        </template>
      </UIButton>
    </div>
    <div v-if="isFetching && !isSearching" class="flex justify-center py-4">
      <Loader />
    </div>
    <div ref="dossiersTable" :class="{ hidden: isLastPage || searchTerm || isFetching }" />
  </div>
</template>
